import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiSysPermissionService } from '@core/api/api-sys-permissions.service';
import { ApiSysService } from '@core/api/api-sys.service';
import { AuthService } from '@core/auth/auth.service';
import { throwApiError } from '@core/error';
import { parseUserTypeId, UserTypeId } from '@shared/models/enums';
import { ModuleAccess } from './models/module-access.model';

@Injectable({ providedIn: 'root' })
export class ModulePermissionsService {
  private readonly apiSysPerm = inject(ApiSysPermissionService);
  private readonly apiSys = inject(ApiSysService);
  private readonly auth = inject(AuthService);

  private readonly _userType = signal<UserTypeId | null>(null);
  private readonly _moduleAccess = signal<ModuleAccess>({});
  private readonly _petitionsCount = signal(0);

  readonly userType = this._userType.asReadonly();
  readonly moduleAccess = this._moduleAccess.asReadonly();
  readonly petitionsCount = this._petitionsCount.asReadonly();

  async loadPermissions(translations?: any): Promise<void> {
    try {
      const res = await firstValueFrom(this.apiSysPerm.getPermissionsMenu(this.auth.getToken()));
      const userType = parseUserTypeId(res.userType);
      this._userType.set(userType);
      this._moduleAccess.set(res.moduleAccess ?? {});

      if (this.shouldLoadPetitionsCount(userType)) {
        await this.loadPetitionsCount(translations);
      } else {
        this._petitionsCount.set(0);
      }
    } catch (error) {
      this._petitionsCount.set(0);
      throwApiError(error as { status: number }, translations, 'FETCH_PERMISSIONS_ERROR');
    }
  }

  /** Public so any future petition-flow code can refresh the badge directly. */
  async loadPetitionsCount(translations?: any): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiSys.countAllUserPetitions(this.auth.getToken()));
      this._petitionsCount.set(response?.data ?? 0);
    } catch (error) {
      this._petitionsCount.set(0);
      throwApiError(error as { status: number }, translations, 'FETCH_PETITIONS_ERROR');
    }
  }

  canAccessModule(module: string): boolean {
    if (this._userType() === UserTypeId.SUPER) return true;

    const access = this._moduleAccess()[module];
    if (typeof access === 'boolean') {
      return access;
    }
    if (typeof access === 'object') {
      return Object.values(access).some(val => val === true);
    }

    return false;
  }

  canAccessSection(module: string, section?: string): boolean {
    if (this._userType() === UserTypeId.SUPER) return true;

    const access = this._moduleAccess()[module];

    if (typeof access === 'object' && access !== null && section !== undefined) {
      return !!access[section];
    }

    if (typeof access === 'boolean') {
      return access;
    }

    return false;
  }

  private shouldLoadPetitionsCount(userType: UserTypeId | null): boolean {
    return userType !== UserTypeId.USER;
  }
}
